import Link from "next/link";
import { Stack } from "@mui/material";

import { routes } from "src/shared/routes";
import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";
import Head from "next/head";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { constants } from "src/shared/constants";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Purchase Colorado Rockies Upgrades",
    description: (
      <>
        Enhance your game-day experience by upgrading your Colorado Rockies
        tickets here. Upgrades are non-refundable. Please use your rockies.com
        email to purchase your tickets.
      </>
    ),
    authRedirectPage: routes.home,
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How come I don't see seat upgrades available for purchase?",
    answer:
      "Upgrades are only eligible for those with a game ticket already. If you do not have a ticket for the game, please go to Rockies Ticket Information | Colorado Rockies to purchase.",
  },
  {
    question: "Will there be seat upgrades available for every game?",
    answer:
      "Upgrades are subject to availability, please check back in on the day of the game to see eligible offers.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "After successful purchase, seat upgrades will be delivered within your Ballpark app. Please note that these tickets are not eligible for entry into Coors Field.",
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Seats will be located in the sections noted in your seat upgrade offer.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer: "Seat upgrades are non transferrable and not eligible for resale.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer: "Seat upgrades are non transferrable and not eligible for resale.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please head to Ticket Resolution for any assistance you may require.",
  },
];

// monthly pass
export const TERMS_AND_CONDITION = () => {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from rockies.com. You may unsubscribe at any time. By
      checking this box you agree to receive additional commercial emails from
      rockies.com and MLB.com.
      <br />
      <br />
      The terms and conditions of your ticket and policies applicable to its use
      are updated to address circumstances related to COVID-19 and health and
      safety. By clicking the Purchase Tickets button, you agree to accept all{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/rockies/tickets/ticket-back-disclaimer"
        }
        target={"_blank"}
      >
        Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      <br />
      <br />
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
        }
        target={"_blank"}
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
        }
        target={"_blank"}
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Rockies account. Message and data rates may apply
      Tickets will be made available in the MLB Ballpark mobile application
      only. MLB.com and supported mobile device required.
    </>
  );
};

// season pass
export const TERMS_AND_CONDITION_OPT_OUT_RESTRICTED = () => {
  return (
    <>
      I understand that I am agreeing to a monthly subscription through the rest
      of the season with no opt out.{" "}
      <b>
        It is a total of $199 broken out into 6 installments of $33.16 billed
        monthly through August 2025
      </b>
      . The Colorado Rockies Ballpark Pass (“Ballpark Pass”) provides access to
      most Colorado Rockies (“Club”) regular season home games during the 2025
      season. Please note excluded games are 4/4 and 5/23-25 and that the
      Ballpark Pass provides for a Standing Room Only ticket. All Ballpark Pass
      tickets are delivered via the MLB Ballpark app on your mobile device.
      Ballpark Passes may only be scanned by the holder of the associated MLB
      Ballpark app account, and cannot be forwarded, shared, exchanged or
      resold. Each Ballpark Pass ticket barcode will be visible in the MLB
      Ballpark app and available to scan beginning approximately two (2) hours
      prior to the first pitch of the respective game. Screenshots will not be
      accepted as a form of entry into Coors Field. Limit of four (4) Ballpark
      Passes may be purchased by any person, household or entity.
      <br />
      <br />
      Ballpark Pass will be automatically charged or renewed each month during
      the 2025 season unless otherwise cancelled as set forth below. By
      purchasing the Ballpark Pass, you authorize the Club to automatically
      charge the Ballpark Pass yearly or monthly fee to the credit or debit card
      specified online in your account, or any subsequent credit or debit
      card(s) you provide to the Club (your “Card”) on a month-by-month basis
      during the 2025 regular season (each charge, a “Charge”). Each Charge of
      your Card of the yearly or monthly fee will occur prior to the month in
      which the Ballpark Pass is eligible. If the Card you provide to the Club
      expires or is not valid, then you must immediately provide the Club with a
      substitute card account or the Club may cancel your Ballpark Pass. On the
      15th day of any calendar month, Monthly Ballpark Pass customers will
      receive a text message notifying you that the opt-out window for managing
      your monthly Ballpark Pass subscription will open. Go to the Manage
      Subscription tab when logged into your account and select the Cancel.
      <br />
      <br />
      The terms and conditions of your Ballpark Pass and policies applicable to
      use are subject to the Colorado Rockies Regular Season Ticket Terms &
      Conditions as well as policies applicable to use. Please visit the
      Ballpark Pass FAQs for additional information. Ticket brokers are not
      eligible for this offer. Any attempt to resell the Ballpark Pass voids the
      Ballpark Pass. All sales are final.{" "}
      <b>There are no refunds or exchanges</b>. Ballpark Passes are limited and
      available while supplies last.
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/rockies/tickets/ticket-back-disclaimer",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        Rockies Terms & Conditions
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 1,
      label: "Rockies Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/rockies/tickets/ticket-back-disclaimer",
          "_self",
        );
      },
    },
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self",
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self",
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1800332046857750');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                try{
                  const { cartRes, checkoutRes, passes } = e.detail;

                  let content_name = "";
                  if(cartRes.products && cartRes.products.length) {
                    content_name = "${TEAM_SCHEMA}_single_game_purchases";
                  };
                  if(cartRes.passes && cartRes.passes.length && passes.length ) {
                    const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                    content_name = passDetail.passTitle;
                  };

                  fbq('track', 'Purchase', {
                    content_name: content_name, 
                    value: String(cartRes.totalAmount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                
                } catch(error){
                  console.log('meta pixel purchase event error:', error); 
                }
              });
            `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=1800332046857750&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};
